'use client';

import { Suspense } from 'react';
import Link from 'next/link';
import { Brain, Upload, Search, Download } from 'lucide-react';
import AuthPanel from './AuthPanel';
import LandingExample from './LandingExample';
import SiteFooter from '../components/SiteFooter';

function LandingPage() {
  return (
    <div className="min-h-screen page-shell flex flex-col">
      <header className="relative z-[2] border-b border-white/[0.06]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Link href="/login" className="flex items-center gap-3 min-w-0 group">
            <span className="page-header-icon !h-10 !w-10 !rounded-xl shrink-0">
              <Brain className="w-5 h-5" strokeWidth={1.75} aria-hidden />
            </span>
            <span className="text-base sm:text-lg font-semibold tracking-tight text-white truncate">
              BrainScraper
            </span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6 text-sm text-gray-400 shrink-0">
            <Link href="/terms" className="hidden sm:inline hover:text-white transition-colors">
              Terms
            </Link>
            <Link href="/privacy" className="hidden sm:inline hover:text-white transition-colors">
              Privacy
            </Link>
            <a
              href="#sign-in"
              className="btn-primary px-3.5 py-2 text-sm shadow-none hover:brightness-105"
            >
              Sign in
            </a>
          </nav>
        </div>
      </header>

      <main className="relative z-[1] flex-1">
        {/* Hero: brand, headline, support, CTA + visual / auth */}
        <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-10 sm:pt-14 pb-12 sm:pb-16">
          <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] gap-10 lg:gap-14 items-start">
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-5 max-w-xl">
                <div className="flex items-center gap-3">
                  <span className="page-header-icon !h-12 !w-12 !rounded-2xl">
                    <Brain className="w-6 h-6" strokeWidth={1.75} aria-hidden />
                  </span>
                  <span className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                    BrainScraper
                  </span>
                </div>
                <h1 className="text-3xl sm:text-[2.5rem] font-semibold tracking-tight text-white/95 text-balance leading-[1.15]">
                  Upload a spreadsheet. Enrich missing data.
                </h1>
                <p className="text-base sm:text-lg text-gray-400 leading-relaxed text-pretty max-w-lg">
                  Start with names and places. BrainScraper fills in the rest — then you filter
                  to the audience you want.
                </p>
                <ul className="flex flex-wrap gap-1.5 pt-1 max-w-full" aria-label="Fields BrainScraper can enrich">
                  {[
                    'Mobile number',
                    'Email',
                    'Address',
                    'Age',
                    'Zipcode',
                    'State',
                    'City',
                    'Gender',
                    'Estimated income',
                  ].map((field) => (
                    <li
                      key={field}
                      className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-gray-300"
                    >
                      {field}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <a
                    href="#sign-in"
                    className="btn-primary inline-flex items-center justify-center px-5 py-3 text-sm"
                  >
                    Get started
                  </a>
                  <a
                    href="#how-it-works"
                    className="inline-flex items-center justify-center px-5 py-3 text-sm rounded-[10px] border border-white/12 text-gray-300 hover:text-white hover:border-white/25 transition-colors"
                  >
                    How it works
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  Pay as you go — <span className="text-gray-300">1¢</span> per enrichment.
                </p>
              </div>

              <LandingExample />
            </div>

            <div id="sign-in" className="lg:sticky lg:top-8 scroll-mt-24 animate-fade-in-up">
              <Suspense
                fallback={
                  <div className="terminal-border rounded-2xl p-8 text-gray-400 text-sm">
                    Loading…
                  </div>
                }
              >
                <AuthPanel />
              </Suspense>
            </div>
          </div>
        </section>

        <section
          id="how-it-works"
          className="border-t border-white/[0.06] scroll-mt-20"
        >
          <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-16">
            <div className="max-w-2xl space-y-3 mb-10">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                How it works
              </h2>
              <p className="text-gray-400 leading-relaxed">
                Scrape or export a starting list. Enrich the gaps. Keep only the rows that fit.
              </p>
            </div>

            <ol className="grid sm:grid-cols-3 gap-8 sm:gap-6">
              {[
                {
                  icon: Upload,
                  title: 'Upload your list',
                  body: 'CSV or spreadsheet of names plus whatever you already have — city, state, ZIP, or a scrape export.',
                },
                {
                  icon: Search,
                  title: 'Enrich the gaps',
                  body: 'Fill missing mobile numbers, emails, address, age, ZIP, state, city, and estimated household income.',
                },
                {
                  icon: Download,
                  title: 'Filter and export',
                  body: 'Drop landlines, VOIP, age, income, or states you don’t want. Download the file or send it to your tools.',
                },
              ].map((step, index) => (
                <li key={step.title} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="page-header-icon !h-10 !w-10 !rounded-xl">
                      <step.icon className="w-4 h-4" strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="text-xs font-semibold tabular-nums text-gray-500">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-white">{step.title}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-t border-white/[0.06]">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 py-14 sm:py-16">
            <div className="max-w-2xl space-y-3 mb-10">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                Scrape the names. Complete the dataset here.
              </h2>
              <p className="text-gray-400 leading-relaxed">
                Tools like Octoparse are for collecting names and cities from public pages and
                social lists. BrainScraper is the step after: upload that file, fill missing
                mobiles, emails, and estimated income, then filter out rows that don’t fit.
              </p>
            </div>

            <dl className="grid sm:grid-cols-2 gap-x-12 gap-y-8 max-w-4xl">
              {[
                {
                  dt: 'Use the scrape you already ran',
                  dd: 'Export names and locations from a scraper, a social list, or a spreadsheet you already keep. You don’t rebuild that pipeline to finish the contacts.',
                },
                {
                  dt: 'Fill what the scrape left blank',
                  dd: 'BrainScraper appends missing mobile numbers, emails, address, age, ZIP, state, city, and estimated household income where a match is available.',
                },
                {
                  dt: 'Filter to the people you want',
                  dd: 'Keep working-age rows, drop landline and VOIP lines, set ZIP household income, or limit states — then export only what remains.',
                },
                {
                  dt: 'One cent per enrichment',
                  dd: 'Wallet billing. Fund what you need and stop anytime. You remain responsible for consent and lawful use of contact data after enrichment.',
                },
              ].map((item) => (
                <div key={item.dt} className="space-y-2">
                  <dt className="text-base font-semibold text-white">{item.dt}</dt>
                  <dd className="text-sm text-gray-400 leading-relaxed">{item.dd}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <SiteFooter contained showSignIn signInHref="#sign-in" />
    </div>
  );
}

export default function LoginPage() {
  return <LandingPage />;
}
